import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from 'axios';

import { proxyAxios } from 'api/apiBase/axios';

import type { RestRequestOptions } from './types';

const PROXY_PATH = '/api/v1/proxy';

const buildUrlWithParams = (url: string, params?: AxiosRequestConfig['params']): string => {
  if (!params || (Array.isArray(params) && params.length === 0)) {
    return url;
  }

  const searchParams = new URLSearchParams();

  const appendParam = (key: string, value: unknown) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => appendParam(key, item));
      return;
    }

    searchParams.append(key, String(value));
  };

  if (typeof params === 'object' && !Array.isArray(params)) {
    Object.entries(params).forEach(([key, value]) => appendParam(key, value));
  }

  const query = searchParams.toString();
  if (!query) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${query}`;
};

const exchangeProxyClient: Pick<AxiosInstance, 'get' | 'post'> = {
  get<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
    const { params, signal, timeout, headers } = config ?? {};
    const targetUrl = buildUrlWithParams(url, params);

    const requestConfig: AxiosRequestConfig = {
      signal,
      timeout,
      headers,
      params: {
        url: targetUrl,
      },
    };

    return proxyAxios.get<T, R>(PROXY_PATH, requestConfig);
  },
  post<T = unknown, R = AxiosResponse<T>>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    const { params, signal, timeout, headers } = config ?? {};
    const targetUrl = buildUrlWithParams(url, params);

    const payload: {
      url: string;
      method: Method;
      data?: unknown;
    } = {
      url: targetUrl,
      method: 'POST',
    };

    if (data !== undefined) {
      payload.data = data;
    }

    const requestConfig: AxiosRequestConfig = {
      signal,
      timeout,
      headers,
    };

    return proxyAxios.post<T, R>(PROXY_PATH, payload, requestConfig);
  },
};

export const proxyRestOptions: RestRequestOptions = {
  client: exchangeProxyClient as AxiosInstance,
};
